import { Badge, BadgeSize } from "shared/components/Badge";
import { Show } from "shared/components";
import type { Address } from "types/landlord/address";
import { STATUS_BADGE } from "shared/app/AddressValidationStatus/statusBadge";

// api only sends the short reason (matches the ticket's example phrasing).
// This maps the known reasons to a fuller, plain-language explanation for
// display — presentation-only, no backend change.
const ISSUE_EXPLANATIONS: Record<string, string> = {
  "Address not found":
    "This street, city and ZIP combination doesn't correspond to a real, deliverable location.",
  "Unit number is missing":
    "This address matches a multi-unit building, but no unit number was provided.",
  "Unit number not recognized":
    "This unit number doesn't match any known unit at this address.",
  "City or state doesn't match the ZIP code":
    "The street and ZIP code point to a real location, but it sits in a different city or state than the one saved here."
};

interface AddressValidationStatusProps {
  address?: Pick<
    Address,
    "address_validation_status" | "address_validation_issue"
  >;
  size?: BadgeSize;
}

export const AddressValidationStatus = ({
  address,
  size = BadgeSize.SM
}: AddressValidationStatusProps) => {
  const status = address?.address_validation_status ?? "not_checked";
  const { variant, label } = STATUS_BADGE[status];
  const issue = address?.address_validation_issue;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={variant} size={size}>
        {label}
      </Badge>
      <Show if={status === "issue_found" && Boolean(issue)}>
        <span className="text-sm text-content-700">
          <span className="font-semibold">{issue}</span>
          {issue && ISSUE_EXPLANATIONS[issue]
            ? ` — ${ISSUE_EXPLANATIONS[issue]}`
            : null}
        </span>
      </Show>
    </div>
  );
};
